/**
 * wishlist.js — lightweight client-side wishlist + compare using localStorage.
 *
 * Phase 1: localStorage-only, no backend sync. Future: sync to /api/customer/wishlist
 * when the user is signed in. Both stores are arrays of product IDs (strings).
 */
const WISH_KEY = 'io_wishlist_v1';
const CMP_KEY = 'io_compare_v1';
const COMPARE_MAX = 4;

let _api = null;
async function getApi() {
    if (!_api) { _api = (await import('@/lib/api')).api; }
    return _api;
}

function read(key) {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); }
    catch { return []; }
}
function write(key, list) {
    localStorage.setItem(key, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(`io-${key}-change`));
}

// ---- Wishlist
export const getWishlist = () => read(WISH_KEY);
export const isWished = (id) => getWishlist().includes(id);
export const toggleWish = (id) => {
    const list = getWishlist();
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1); else list.push(id);
    write(WISH_KEY, list);
    // Best-effort backend sync (silent for anon users)
    if (localStorage.getItem('io_token')) {
        getApi().then((api) => api.put('/customer/wishlist', { ids: list }).catch(() => {}));
    }
    return list.includes(id);
};

// ---- Compare
export const getCompare = () => read(CMP_KEY);
export const inCompare = (id) => getCompare().includes(id);
export const toggleCompare = (id) => {
    const list = getCompare();
    const i = list.indexOf(id);
    if (i >= 0) { list.splice(i, 1); }
    else {
        if (list.length >= COMPARE_MAX) return { ok: false, reason: 'max' };
        list.push(id);
    }
    write(CMP_KEY, list);
    return { ok: true, present: list.includes(id) };
};
export const clearCompare = () => write(CMP_KEY, []);

// ---- Subscribe helper for components
export function subscribe(key, cb) {
    const handler = () => cb();
    window.addEventListener(`io-${key}-change`, handler);
    window.addEventListener('storage', handler);
    return () => {
        window.removeEventListener(`io-${key}-change`, handler);
        window.removeEventListener('storage', handler);
    };
}

// ---- Backend sync (called by AuthProvider when signing in/out)
export async function pullWishlistFromBackend() {
    try {
        const api = await getApi();
        const { data } = await api.get('/customer/wishlist');
        const local = read(WISH_KEY);
        const merged = Array.from(new Set([...(data?.ids || []), ...local]));
        write(WISH_KEY, merged);
        await api.put('/customer/wishlist', { ids: merged });
    } catch { /* ignore — anonymous browsers stay local-only */ }
}

export async function pushWishlistToBackend() {
    try {
        const api = await getApi();
        await api.put('/customer/wishlist', { ids: read(WISH_KEY) });
    } catch { /* ignore */ }
}
export const WISH_KEY_NAME = WISH_KEY;
export const CMP_KEY_NAME = CMP_KEY;
export { COMPARE_MAX };
